/**
 * Governance Hub — Correspondence (operator order 2026-09-03).
 * Every email Workstream sent about a calendar or state-contract item, as a
 * thread: the note on top, follow-ups and the recipient's responses beneath,
 * and a reply box that sends through the same Workstream path (a follow-up
 * on the thread). Read view over existing records; replying reuses the
 * existing follow-up machinery, nothing new is invented.
 */
import React, { useEffect, useState, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');

const defaultChip = (module) => (module === 'smac_obligation' ? 'State contract' : 'Calendar');

export default function HubCorrespondence({
  base = '/api/calendar/correspondence',
  item = null,
  chipFor = defaultChip,
  intro = 'Every email sent from Workstream about a calendar or state-contract item, with its follow-ups and responses in one thread. Reply here and it goes out the same way, into the same thread.',
} = {}) {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState({});
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState({});

  const load = useCallback(() => {
    fetch(`${API}${base}${item ? `?item=${encodeURIComponent(item)}` : ''}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => setData(d.success ? d : { mails: [], responses: [] }))
      .catch(() => setData({ mails: [], responses: [] }));
  }, [base, item]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="text-sm text-slate-500 py-8">Loading correspondence…</p>;

  const mails = data.mails || [];
  const roots = mails.filter((m) => !m.parent_outreach_id);
  const childrenOf = (id) => mails.filter((m) => m.parent_outreach_id === id).sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));
  const responsesOf = (id) => (data.responses || []).filter((r) => r.outreach_id === id);
  const emailRepliesOf = (id) => (data.email_replies || []).filter((r) => r.outreach_id === id);

  const sendReply = async (root) => {
    const text = (reply[root.id] || '').trim();
    if (text.length < 3) { setNotice((n) => ({ ...n, [root.id]: 'Write the reply first.' })); return; }
    setBusy(root.id); setNotice((n) => ({ ...n, [root.id]: '' }));
    try {
      const thread = [root, ...childrenOf(root.id)];
      const latest = thread[thread.length - 1];
      const d = await fetch(`${API}/api/workstream/outreach/${latest.id}/renudge`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intent: text }),
      }).then((r) => r.json());
      if (d.success) { setReply((x) => ({ ...x, [root.id]: '' })); setNotice((n) => ({ ...n, [root.id]: 'Sent. It will appear in this thread.' })); setTimeout(load, 1200); }
      else setNotice((n) => ({ ...n, [root.id]: d.message || 'That did not go through.' }));
    } catch { setNotice((n) => ({ ...n, [root.id]: 'That did not go through. Try again.' })); }
    setBusy(null);
  };

  const MailCard = ({ m, isChild }) => (
    <div className={`rounded-xl bg-white ring-1 ring-slate-200 p-4 ${isChild ? 'ml-6 mt-2' : ''}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-800">{m.subject}</p>
          <p className="text-xs text-slate-500 mt-0.5">To {m.recipient_name || m.recipient_email} · from {m.sender_name || m.sender_email} · {fmt(m.sent_at)}</p>
          <div className="flex gap-1.5 flex-wrap mt-1.5">
            {(m.items || []).map((it, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">
                {chipFor(it.module)} · {String(it.title || '').slice(0, 50)}
              </span>
            ))}
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.status === 'replied' ? 'bg-emerald-50 text-emerald-700' : m.status === 'clicked' ? 'bg-sky-50 text-sky-700' : 'bg-slate-100 text-slate-600'}`}>
          {m.status === 'replied' ? 'Replied' : m.status === 'clicked' ? 'Opened' : m.status === 'renudged' ? 'Followed up' : 'Sent'}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-3">
        <button type="button" onClick={() => setOpenId(openId === m.id ? null : m.id)} className="text-xs font-semibold text-slate-500 hover:text-slate-700">
          {openId === m.id ? 'Hide the email' : 'Read the email'}
        </button>
        <a href={`${base}/${m.id}/eml`} download className="text-xs font-semibold text-slate-500 hover:text-slate-700">
          Download the email
        </a>
      </div>
      {openId === m.id && <p className="mt-2 text-[13px] text-slate-700 whitespace-pre-wrap leading-relaxed border-t border-slate-100 pt-2">{m.body_text}</p>}
      {emailRepliesOf(m.id).length > 0 ? emailRepliesOf(m.id).map((r, i) => (
        <div key={i} className="mt-2 bg-emerald-50 rounded-lg px-3 py-2">
          <p className="text-[11px] font-semibold text-emerald-800">{r.from_email} replied · {fmt(r.created_at)}</p>
          <p className="text-[12.5px] text-emerald-900 whitespace-pre-wrap leading-relaxed mt-1">{r.body}</p>
        </div>
      )) : m.reply_excerpt ? (
        <p className="mt-2 text-[12.5px] text-emerald-800 bg-emerald-50 rounded-lg px-3 py-2">Their reply: {m.reply_excerpt}</p>
      ) : null}
      {responsesOf(m.id).map((r, i) => (
        <p key={i} className="mt-1.5 text-[12.5px] text-slate-600 border-l-2 border-slate-200 pl-3">
          {r.responder_email || 'Recipient'} {r.action === 'commented' ? 'commented' : r.action === 'acknowledged' ? 'acknowledged' : r.action === 'marked_done' ? 'marked it done' : 'snoozed it'}{r.comment ? `: ${r.comment}` : ''} · {fmt(r.created_at)}
        </p>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-500">{intro}</p>
      {roots.length === 0 && <p className="text-sm text-slate-500 py-6">No correspondence yet. When Workstream sends a note about one of this screen's items, it lands here.</p>}
      {roots.map((root) => (
        <div key={root.id}>
          <MailCard m={root} />
          {childrenOf(root.id).map((c) => <MailCard key={c.id} m={c} isChild />)}
          <div className="ml-6 mt-2 flex gap-2 items-start">
            <textarea value={reply[root.id] || ''} onChange={(e) => setReply((x) => ({ ...x, [root.id]: e.target.value }))}
              rows={2} placeholder="Write a reply — it sends as a follow-up on this thread…"
              className="flex-1 rounded-xl ring-1 ring-slate-200 px-3.5 py-2 text-[13px] outline-none focus:ring-2 focus:ring-orange-300 bg-white" />
            <button type="button" disabled={busy === root.id} onClick={() => sendReply(root)}
              className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy === root.id ? 'Sending…' : 'Reply'}
            </button>
          </div>
          {notice[root.id] && <p className="ml-6 mt-1 text-[12px] font-medium text-slate-600">{notice[root.id]}</p>}
        </div>
      ))}
    </div>
  );
}
